import { useState, useEffect, useCallback } from 'react';
import { supabase, TABLES } from '../utils/supabase';
import type { User, UserProfileData } from '../types';

// Hook for user authentication and profile management
export const useUsers = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cache for user lookups to reduce redundant queries
  const userCache: Map<string, { user: User | null; timestamp: number }> = new Map();
  const USER_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache

  // Get user by deployer address
  const getUserByWallet = async (walletAddress: string): Promise<User | null> => {
    try {
      const normalizedAddress = walletAddress.toLowerCase();
      
      // Check cache first
      const cached = userCache.get(normalizedAddress);
      if (cached && Date.now() - cached.timestamp < USER_CACHE_DURATION) {
        return cached.user;
      }
      
      // Try case-insensitive match directly (more efficient)
      const { data: user, error: userError } = await supabase
        .from(TABLES.USERS)
        .select('*')
        .eq('deployer_address', normalizedAddress)
        .single();

      // Handle 406 Not Acceptable error - likely an API configuration issue
      if (userError) {
        if (userError.code === 'PGRST116') {
          // No user found - cache null result
          userCache.set(normalizedAddress, { user: null, timestamp: Date.now() });
          return null;
        }
        
        // For 406 or other errors, log but don't throw - return null gracefully
        if (userError.message?.includes('406') || userError.code === 'PGRST302') {
          console.warn(`[getUserByWallet] API error for ${normalizedAddress}: ${userError.code} - ${userError.message}`);
          userCache.set(normalizedAddress, { user: null, timestamp: Date.now() });
          return null;
        }
        
        // For other errors, still cache null to prevent repeated failures
        console.error(`[getUserByWallet] Error fetching user for ${normalizedAddress}:`, userError);
        userCache.set(normalizedAddress, { user: null, timestamp: Date.now() });
        return null;
      }

      // Cache successful lookup
      userCache.set(normalizedAddress, { user: user, timestamp: Date.now() });
      return user;
    } catch (err) {
      console.error('Error fetching user by wallet:', err);
      // Cache null to prevent repeated failures
      const normalizedAddress = walletAddress.toLowerCase();
      userCache.set(normalizedAddress, { user: null, timestamp: Date.now() });
      return null;
    }
  };

  // Create or update user
  const createOrUpdateUser = async (walletAddress: string, username: string): Promise<User> => {
    try {
      setLoading(true);
      setError(null);

      // Check if username is already taken
      const { data: existingUser } = await supabase
        .from(TABLES.USERS)
        .select('username')
        .eq('username', username)
        .neq('deployer_address', walletAddress.toLowerCase())
        .single();

      if (existingUser) {
        throw new Error('Username is already taken');
      }

      // Upsert user
      const { data, error } = await supabase
        .from(TABLES.USERS)
        .upsert({
          deployer_address: walletAddress.toLowerCase(),
          username
        }, {
          onConflict: 'deployer_address'
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      setCurrentUser(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create/update user';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if username is available
  const checkUsernameAvailability = async (username: string, currentWallet?: string): Promise<boolean> => {
    try {
      let query = supabase
        .from(TABLES.USERS)
        .select('username')
        .eq('username', username);

      // If checking for current user, exclude their own wallet
      if (currentWallet) {
        query = query.neq('deployer_address', currentWallet.toLowerCase());
      }

      const { error } = await query.single();

      if (error && error.code === 'PGRST116') {
        // No user found, username is available
        return true;
      }

      // Username is taken
      return false;
    } catch (err) {
      console.error('Error checking username availability:', err);
      return false;
    }
  };

  // Get user profile with stats
  const getUserProfile = async (walletAddress: string): Promise<UserProfileData | null> => {
    try {
      const user = await getUserByWallet(walletAddress);
      if (!user) return null;

      // Get favorites count
      const { count: favoritesCount } = await supabase
        .from(TABLES.USER_FAVORITES)
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Get created tokens count
      const { count: createdTokensCount } = await supabase
        .from(TABLES.TOKENS)
        .select('*', { count: 'exact', head: true })
        .eq('deployer_address', walletAddress.toLowerCase());

      return {
        ...user,
        favorites_count: favoritesCount || 0,
        created_tokens_count: createdTokensCount || 0
      };
    } catch (err) {
      console.error('Error fetching user profile:', err);
      throw err;
    }
  };

  // Set current user when wallet connects
  const setCurrentUserByWallet = useCallback(async (walletAddress: string) => {
    try {
      setLoading(true);
      const user = await getUserByWallet(walletAddress);
      setCurrentUser(user);
    } catch (err) {
      console.error('Error setting current user:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user');
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear current user when wallet disconnects
  const clearCurrentUser = useCallback(() => {
    setCurrentUser(null);
    setError(null);
  }, []);

  return {
    currentUser,
    loading,
    error,
    getUserByWallet,
    createOrUpdateUser,
    checkUsernameAvailability,
    getUserProfile,
    setCurrentUserByWallet,
    clearCurrentUser
  };
};


// Hook for token favorites management
export const useTokenFavorites = (userDeployerAddress?: string) => {
  const [userFavorites, setUserFavorites] = useState<Set<string>>(new Set());
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const [favoritesError, setFavoritesError] = useState<string | null>(null);

  const fetchUserFavorites = useCallback(async () => {
    if (!userDeployerAddress) {
      setUserFavorites(new Set());
      setLoadingFavorites(false);
      return;
    }

    setLoadingFavorites(true);
    setFavoritesError(null);

    try {
      const { data: user, error: userError } = await supabase
        .from(TABLES.USERS)
        .select('id')
        .eq('deployer_address', userDeployerAddress.toLowerCase())
        .single();

      if (userError) {
        if (userError.code === 'PGRST116') {
          setUserFavorites(new Set());
          setLoadingFavorites(false);
          return;
        }
        throw new Error(userError.message);
      }

      if (!user) {
        setUserFavorites(new Set());
        setLoadingFavorites(false);
        return;
      }

      const { data, error } = await supabase
        .from(TABLES.USER_FAVORITES)
        .select('token_id')
        .eq('user_id', user.id);

      if (error) {
        throw new Error(error.message);
      }

      const favoritedTokenIds = new Set(data.map(fav => fav.token_id));
      setUserFavorites(favoritedTokenIds);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user favorites';
      setFavoritesError(errorMessage);
      setUserFavorites(new Set());
    } finally {
      setLoadingFavorites(false);
    }
  }, [userDeployerAddress]);

  useEffect(() => {
    fetchUserFavorites();
  }, [fetchUserFavorites]);

  const toggleFavorite = useCallback(async (tokenId: string, isCurrentlyFavorited: boolean) => {
    if (!userDeployerAddress) {
      setFavoritesError('User not connected');
      return false;
    }

    console.log('🔄 toggleFavorite called:', { tokenId, isCurrentlyFavorited, userDeployerAddress });

    try {
      let success = false;
      if (isCurrentlyFavorited) {
        console.log('➖ Removing favorite via RPC...');
        const { data, error } = await supabase.rpc('remove_favorite', {
          user_wallet: userDeployerAddress.toLowerCase(),
          token_uuid: tokenId
        });
        console.log('➖ Remove favorite response:', { data, error });
        if (error) throw new Error(`Remove favorite error: ${error.message}`);
        success = data;
      } else {
        console.log('➕ Adding favorite via RPC...');
        const { data, error } = await supabase.rpc('add_favorite', {
          user_wallet: userDeployerAddress.toLowerCase(),
          token_uuid: tokenId
        });
        console.log('➕ Add favorite response:', { data, error });
        
        // If user not found, create the user record and try again
        if (error && error.message.includes('not found')) {
          console.log('🔧 User not found, creating user record...');
          try {
            // Create user record with just the deployer address
            const { error: createError } = await supabase
              .from(TABLES.USERS)
              .insert({
                deployer_address: userDeployerAddress.toLowerCase(),
                username: userDeployerAddress.slice(-6) // Last 6 chars only (meets 1-8 limit, avoids reserved words)
              })
              .select()
              .single();
              
            if (createError) {
              console.log('❌ Failed to create user:', createError);
              throw new Error(`Failed to create user: ${createError.message}`);
            }
            
            console.log('✅ User created, retrying favorite...');
            // Retry the favorite operation
            const { data: retryData, error: retryError } = await supabase.rpc('add_favorite', {
              user_wallet: userDeployerAddress.toLowerCase(),
              token_uuid: tokenId
            });
            console.log('🔄 Retry favorite response:', { retryData, retryError });
            
            if (retryError) throw new Error(`Retry favorite error: ${retryError.message}`);
            success = retryData;
          } catch (createErr) {
            throw new Error(`User creation failed: ${createErr instanceof Error ? createErr.message : 'Unknown error'}`);
          }
        } else if (error) {
          throw new Error(`Add favorite error: ${error.message}`);
        } else {
          success = data;
        }
      }

      console.log('✨ RPC success result:', success);

      if (success) {
        // Optimistically update UI
        setUserFavorites(prev => {
          const newSet = new Set(prev);
          if (isCurrentlyFavorited) {
            newSet.delete(tokenId);
          } else {
            newSet.add(tokenId);
          }
          return newSet;
        });
        // Re-fetch to ensure consistency
        fetchUserFavorites();
      }
      return success;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle favorite';
      console.error('❌ toggleFavorite error:', err);
      setFavoritesError(errorMessage);
      return false;
    }
  }, [userDeployerAddress, fetchUserFavorites]);

  return { userFavorites, loadingFavorites, favoritesError, toggleFavorite, fetchUserFavorites };
};

// Chain type for favorites
export type FavoriteChain = 'ink' | 'eth' | 'wlfi' | 'mon' | 'sol';

// Mapping of chain to token table name
const CHAIN_TABLE_MAP: Record<FavoriteChain, string> = {
  ink: 'tokens',
  eth: 'tokens_eth',
  wlfi: 'tokens_usd1',
  mon: 'tokens_monad',
  sol: 'tokens_sol',
};

// Single unified favorites table with chain column
const UNIFIED_FAVORITES_TABLE = 'user_favorites';

// Hook for getting top favorited tokens (original for backward compatibility)
export const useTopFavoritedTokens = (limit: number = 5) => {
  const [topTokens, setTopTokens] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTopFavoritedTokens = async () => {
    try {
      setLoading(true);
      setError(null);

      // First get the top favorited tokens from RPC
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_top_favorited_tokens_simple', { limit_count: limit });

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      if (!rpcData || rpcData.length === 0) {
        setTopTokens([]);
        return;
      }

      // Then fetch full token data including description for each token
      const tokenIds = rpcData.map((token: any) => token.id);
      const { data: fullTokenData, error: tokenError } = await supabase
        .from(TABLES.TOKENS)
        .select('*')
        .in('id', tokenIds);

      if (tokenError) {
        console.warn('Failed to fetch full token data, using RPC data:', tokenError);
        setTopTokens(rpcData || []);
        return;
      }

      // Merge RPC data with full token data, preserving the order from RPC
      const mergedData = rpcData.map((rpcToken: any) => {
        const fullToken = fullTokenData?.find((t: any) => t.id === rpcToken.id);
        return {
          ...rpcToken,
          ...fullToken, // This will include description, deployer_address, etc.
          // Preserve RPC-specific fields
          favorites_count: rpcToken.favorites_count
        };
      });

      console.log('🔍 [useTopFavoritedTokens] Merged data:', {
        rpcDataCount: rpcData.length,
        fullTokenDataCount: fullTokenData?.length || 0,
        mergedDataCount: mergedData.length,
        sampleToken: mergedData[0] ? {
          id: mergedData[0].id,
          name: mergedData[0].name,
          hasDescription: !!mergedData[0].description,
          description: mergedData[0].description,
          hasDeployerAddress: !!mergedData[0].deployer_address,
          deployerAddress: mergedData[0].deployer_address
        } : 'No data'
      });

      setTopTokens(mergedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch top favorited tokens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopFavoritedTokens();
  }, [limit]);

  return {
    topTokens,
    loading,
    error,
    refetch: fetchTopFavoritedTokens
  };
};

// NEW: Chain-aware hook for getting top favorited tokens
// Uses unified user_favorites table with chain column
export const useTopFavoritedTokensByChain = (chain: FavoriteChain, limit: number = 5) => {
  const [topTokens, setTopTokens] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTopFavoritedTokens = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const tokenTable = CHAIN_TABLE_MAP[chain];

      // For Ink chain without chain column, use the existing RPC function for backward compatibility
      if (chain === 'ink') {
        try {
          const { data: rpcData, error: rpcError } = await supabase
            .rpc('get_top_favorited_tokens_simple', { limit_count: limit });

          if (!rpcError && rpcData && rpcData.length > 0) {
            // Fetch full token data
            const tokenIds = rpcData.map((token: any) => token.id);
            const { data: fullTokenData } = await supabase
              .from(TABLES.TOKENS)
              .select('*')
              .in('id', tokenIds);

            const mergedData = rpcData.map((rpcToken: any) => {
              const fullToken = fullTokenData?.find((t: any) => t.id === rpcToken.id);
              return { ...rpcToken, ...fullToken, favorites_count: rpcToken.favorites_count };
            });

            setTopTokens(mergedData);
            return;
          }
        } catch (rpcErr) {
          console.log('[useTopFavoritedTokensByChain] RPC fallback failed, trying unified table');
        }
      }

      // Query unified favorites table with chain filter
      const { data: favoritesData, error: favError } = await supabase
        .from(UNIFIED_FAVORITES_TABLE)
        .select('token_id')
        .eq('chain', chain);

      if (!favError && favoritesData && favoritesData.length > 0) {
        // Count favorites per token
        const favoriteCounts: Record<string, number> = {};
        favoritesData.forEach((fav: any) => {
          favoriteCounts[fav.token_id] = (favoriteCounts[fav.token_id] || 0) + 1;
        });

        // Get top token IDs sorted by favorites count
        const sortedTokenIds = Object.entries(favoriteCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, limit)
          .map(([id]) => id);

        if (sortedTokenIds.length > 0) {
          // Fetch full token data from the chain's token table
          const { data: tokenData, error: tokenError } = await supabase
            .from(tokenTable)
            .select('*')
            .in('id', sortedTokenIds);

          if (!tokenError && tokenData) {
            const tokensWithCounts = tokenData.map(token => ({
              ...token,
              favorites_count: favoriteCounts[token.id] || 0
            })).sort((a, b) => b.favorites_count - a.favorites_count);

            setTopTokens(tokensWithCounts);
            return;
          }
        }
      }

      // Fallback: Get tokens ordered by favorites_count or market_cap from token table
      const { data: fallbackData, error: fallbackError } = await supabase
        .from(tokenTable)
        .select('*')
        .order('favorites_count', { ascending: false, nullsFirst: false })
        .limit(limit);

      if (fallbackError) {
        // If favorites_count doesn't exist, try market_cap
        const { data: marketCapData, error: marketCapError } = await supabase
          .from(tokenTable)
          .select('*')
          .order('market_cap_usd', { ascending: false, nullsFirst: false })
          .limit(limit);

        if (marketCapError) {
          throw new Error(marketCapError.message);
        }
        
        setTopTokens(marketCapData || []);
        return;
      }

      setTopTokens(fallbackData || []);
    } catch (err) {
      console.error(`[useTopFavoritedTokensByChain] Error for chain ${chain}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to fetch top favorited tokens');
      setTopTokens([]);
    } finally {
      setLoading(false);
    }
  }, [chain, limit]);

  useEffect(() => {
    fetchTopFavoritedTokens();
  }, [fetchTopFavoritedTokens]);

  return {
    topTokens,
    loading,
    error,
    refetch: fetchTopFavoritedTokens
  };
};

// NEW: Chain-aware token favorites hook
// Uses unified user_favorites table with chain column
export const useTokenFavoritesByChain = (chain: FavoriteChain, userDeployerAddress?: string) => {
  const [userFavorites, setUserFavorites] = useState<Set<string>>(new Set());
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const [favoritesError, setFavoritesError] = useState<string | null>(null);

  const fetchUserFavorites = useCallback(async () => {
    if (!userDeployerAddress) {
      setUserFavorites(new Set());
      setLoadingFavorites(false);
      return;
    }

    setLoadingFavorites(true);
    setFavoritesError(null);

    try {
      // Get user ID
      const { data: user, error: userError } = await supabase
        .from(TABLES.USERS)
        .select('id')
        .eq('deployer_address', userDeployerAddress.toLowerCase())
        .single();

      if (userError) {
        if (userError.code === 'PGRST116') {
          setUserFavorites(new Set());
          setLoadingFavorites(false);
          return;
        }
        throw new Error(userError.message);
      }

      if (!user) {
        setUserFavorites(new Set());
        setLoadingFavorites(false);
        return;
      }

      // Query unified favorites table with chain filter
      const { data, error } = await supabase
        .from(UNIFIED_FAVORITES_TABLE)
        .select('token_id')
        .eq('user_id', user.id)
        .eq('chain', chain);

      if (error) {
        // If chain column doesn't exist yet, fall back to no chain filter for 'ink'
        if (chain === 'ink') {
          const { data: inkData, error: inkError } = await supabase
            .from(UNIFIED_FAVORITES_TABLE)
            .select('token_id')
            .eq('user_id', user.id);
          
          if (!inkError && inkData) {
            setUserFavorites(new Set(inkData.map(fav => fav.token_id)));
            return;
          }
        }
        throw new Error(error.message);
      }

      setUserFavorites(new Set(data.map(fav => fav.token_id)));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user favorites';
      setFavoritesError(errorMessage);
      setUserFavorites(new Set());
    } finally {
      setLoadingFavorites(false);
    }
  }, [userDeployerAddress, chain]);

  useEffect(() => {
    fetchUserFavorites();
  }, [fetchUserFavorites]);

  const toggleFavorite = useCallback(async (tokenId: string, isCurrentlyFavorited: boolean) => {
    if (!userDeployerAddress) {
      setFavoritesError('User not connected');
      return false;
    }

    console.log(`🔄 toggleFavorite [${chain}]:`, { tokenId, isCurrentlyFavorited, userDeployerAddress });

    try {
      // First get or create user ID
      let userId: string;
      const { data: user, error: userError } = await supabase
        .from(TABLES.USERS)
        .select('id')
        .eq('deployer_address', userDeployerAddress.toLowerCase())
        .single();

      if (userError && userError.code === 'PGRST116') {
        // Create user if not exists
        const { data: newUser, error: createError } = await supabase
          .from(TABLES.USERS)
          .insert({
            deployer_address: userDeployerAddress.toLowerCase(),
            username: userDeployerAddress.slice(-6)
          })
          .select()
          .single();
          
        if (createError) throw new Error(`Failed to create user: ${createError.message}`);
        userId = newUser.id;
      } else if (userError) {
        throw new Error(userError.message);
      } else {
        userId = user.id;
      }

      // For Ink chain, try RPC first for backward compatibility, then fall back to direct table
      if (chain === 'ink') {
        try {
          let success = false;
          if (isCurrentlyFavorited) {
            const { data, error } = await supabase.rpc('remove_favorite', {
              user_wallet: userDeployerAddress.toLowerCase(),
              token_uuid: tokenId
            });
            if (!error) success = data;
          } else {
            const { data, error } = await supabase.rpc('add_favorite', {
              user_wallet: userDeployerAddress.toLowerCase(),
              token_uuid: tokenId
            });
            if (!error) success = data;
          }

          if (success) {
            setUserFavorites(prev => {
              const newSet = new Set(prev);
              if (isCurrentlyFavorited) newSet.delete(tokenId);
              else newSet.add(tokenId);
              return newSet;
            });
            return true;
          }
        } catch (rpcErr) {
          console.log('[toggleFavorite] RPC failed, falling back to direct table operation');
        }
      }

      // Use direct table operations with chain column
      if (isCurrentlyFavorited) {
        // Remove favorite - try with chain first, then without
        const { error: chainError } = await supabase
          .from(UNIFIED_FAVORITES_TABLE)
          .delete()
          .eq('user_id', userId)
          .eq('token_id', tokenId)
          .eq('chain', chain);

        if (chainError) {
          console.log(`[toggleFavorite] Delete with chain failed:`, chainError.message);
          // Try without chain column for backward compatibility
          const { error: fallbackError } = await supabase
            .from(UNIFIED_FAVORITES_TABLE)
            .delete()
            .eq('user_id', userId)
            .eq('token_id', tokenId);
          
          if (fallbackError) {
            console.error(`[toggleFavorite] Error removing favorite:`, fallbackError);
          } else {
            console.log(`✅ [toggleFavorite] Removed favorite without chain column`);
          }
        } else {
          console.log(`✅ [toggleFavorite] Removed favorite with chain=${chain}`);
        }
      } else {
        // Add favorite - try multiple approaches for compatibility
        let insertSuccess = false;
        
        // First, try with chain column
        const { error: chainError } = await supabase
          .from(UNIFIED_FAVORITES_TABLE)
          .insert({
            user_id: userId,
            token_id: tokenId,
            chain: chain,
            created_at: new Date().toISOString()
          });

        if (!chainError) {
          insertSuccess = true;
          console.log(`✅ [toggleFavorite] Added favorite with chain=${chain}`);
        } else {
          console.log(`[toggleFavorite] Insert with chain failed:`, chainError.message, chainError.code);
          
          // If it's a 409 conflict or chain column doesn't exist, try without chain
          if (chainError.code === '23505' || chainError.code === 'PGRST204' || 
              chainError.message?.includes('chain') || chainError.message?.includes('column') ||
              chainError.code === '42703') {
            
            // Try without chain column (for backward compatibility)
            const { error: noChainError } = await supabase
              .from(UNIFIED_FAVORITES_TABLE)
              .insert({
                user_id: userId,
                token_id: tokenId,
                created_at: new Date().toISOString()
              });

            if (!noChainError) {
              insertSuccess = true;
              console.log(`✅ [toggleFavorite] Added favorite without chain column`);
            } else if (noChainError.code === '23505') {
              // Already exists - that's fine, treat as success
              insertSuccess = true;
              console.log(`[toggleFavorite] Favorite already exists, treating as success`);
            } else {
              console.error(`[toggleFavorite] Error adding favorite (no chain):`, noChainError);
            }
          } else if (chainError.code === '23505') {
            // Already exists with chain - that's fine
            insertSuccess = true;
            console.log(`[toggleFavorite] Favorite already exists with chain, treating as success`);
          } else {
            console.error(`[toggleFavorite] Error adding favorite:`, chainError);
          }
        }
        
        if (!insertSuccess) {
          console.warn(`[toggleFavorite] Could not add favorite. You may need to add the 'chain' column to user_favorites table.`);
        }
      }

      // Update UI optimistically
      setUserFavorites(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyFavorited) newSet.delete(tokenId);
        else newSet.add(tokenId);
        return newSet;
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle favorite';
      console.error(`❌ toggleFavorite [${chain}] error:`, err);
      setFavoritesError(errorMessage);
      return false;
    }
  }, [userDeployerAddress, chain, fetchUserFavorites]);

  return { userFavorites, loadingFavorites, favoritesError, toggleFavorite, fetchUserFavorites };
};
